import { memo, useRef } from "react"

import { Column, Flex } from "@/atoms"
import { getDate } from "@/helpers/date.helpers"
import { TIME_FILTERS } from "@/helpers/filters.helper"
import { capitalizeFirstLetter } from "@/helpers/string.helpers"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useTranslation } from "@/hooks/useTranslation"
import { LegendChart } from "@/molecules/LegendChart"
import { ColumnTooltip } from "@/pages/DashboardPage/components/molecules/Tooltip"

import type { AgeOpenIssueWidgetProps } from "."

export const AgeOpenIssueChart = memo(
  function AgeOpenIssueChartComponent(props: AgeOpenIssueWidgetProps) {
    const { max, data = [] } = props
    const { t } = useTranslation()
    const { abbreviateNumber } = useNumberConvertor()
    const colors = useRef(getComputedStyle(document.documentElement)).current
    const getColor = (number: number) =>
      colors.getPropertyValue(`--sunset-orange-${number}-color`)
    const range = [getColor(3), getColor(5), getColor(7), getColor(9)]
    const redirect = useRef(true)
    const yMax = Math.round(max * 1.25)

    return (
      <Flex vertical gap={8} className="m-w-0-ks w-100-ks">
        <Flex className="w-100-ks" justify="end">
          <LegendChart
            gap={14}
            items={[...new Set(data.map(({ period }) => period))].map(
              (name, i) => ({
                color: range[i],
                label: `${name} ${t("general.day_other")}`
              })
            )}
          />
        </Flex>
        <div className="w-100-ks">
          <Column
            autoFit
            isGroup
            legend={false}
            xField="severity"
            yField="value"
            seriesField="period"
            data={data}
            height={200}
            style={{ width: "100%" }}
            dodgePadding={2}
            color={range}
            xAxis={{
              label: {
                formatter: (severity: string) =>
                  t(`vulnerabilities.${severity}`)
              }
            }}
            yAxis={{
              min: 0,
              max: yMax === max ? yMax + 4 : yMax,
              label: {
                formatter: (label: string) => {
                  const value = Number(label)

                  if (!value) return value

                  return `${abbreviateNumber(value)}`
                }
              }
            }}
            columnStyle={() => {
              const radius = 4
              const result = {
                radius: [radius, radius]
              }

              return result
            }}
            interactions={[{ type: "element-highlight" }]}
            state={{
              active: {
                style: {
                  stroke: "transparent",
                  cursor: "pointer"
                }
              },
              inactive: {
                style: {
                  fillOpacity: 0.7
                }
              }
            }}
            tooltip={{
              customContent: (severity, datum) => {
                if (!datum?.length) return null

                return (
                  <ColumnTooltip
                    title={`${capitalizeFirstLetter(severity)} ${capitalizeFirstLetter(t("general.severity"))} ${capitalizeFirstLetter(t("general.issue_other"))}`}
                    items={datum.map((values) => ({
                      color: values.color,
                      label: `${values.name || ""} ${t("general.day_other")}`,
                      value: values.data.value
                    }))}
                  />
                )
              }
            }}
            onReady={(plot) => {
              plot.on(
                "click",
                ({
                  data
                }: {
                  data: {
                    data: { severity: string; periodRange: string }
                  }
                }) => {
                  const { severity, periodRange } = data?.data || {}

                  if (!severity) return

                  if (redirect.current)
                    queueMicrotask(() => {
                      const [start, end] = periodRange.split("-")

                      props.redirectToTriage({
                        issueStatus: "open",
                        [TIME_FILTERS.DISCOVERED]: `${getDate()
                          .subtract(Number(end), "d")
                          .local()
                          .startOf("day")
                          .unix()},${getDate().subtract(Number(start), "d").local().endOf("day").unix()}`,
                        severity: capitalizeFirstLetter(
                          severity.toLocaleLowerCase()
                        )
                      })
                    })

                  redirect.current = false
                }
              )
            }}
          />
        </div>
      </Flex>
    )
  },
  (prev, next) => prev.data === next.data
)
